"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getKicadFileType = getKicadFileType;
exports.validateKicadFile = validateKicadFile;
exports.detectKicadProjects = detectKicadProjects;
const project_detection_1 = require("../project-detection");
const boardValidate_1 = require("./boardValidate");
const validateKicadV6SchemaFile_1 = require("./common/validateKicadV6SchemaFile");
const constants_1 = require("./constants");
const projectValidation_1 = require("./projectValidation");
const v6schValidate_1 = require("./schematic/v6schValidate");
const kicad_file_type_1 = require("./kicad-file-type");
const kicadNetlistValidation_1 = require("./kicadNetlistValidation");
function getKicadFileType(filename, fileContents) {
    const path = filename.split('/');
    if (path.length < 1) {
        return false;
    }
    const parts = path[path.length - 1].split('.');
    if (parts.length < 1) {
        return false;
    }
    let ext = parts[parts.length - 1].toLowerCase();
    if (parts.length > 1) {
        ext = '.' + ext;
    }
    switch (ext) {
        case '.kicad_pcb':
            if (fileContents.startsWith(constants_1.KicadPcbPreamble)) {
                return kicad_file_type_1.KicadFileType.KicadPcb;
            }
            break;
        case '.kicad_sch':
            if (fileContents.startsWith(constants_1.KicadSchPreamble)) {
                return kicad_file_type_1.KicadFileType.KicadSch;
            }
            break;
        case '.sch':
            if (fileContents.startsWith(constants_1.EESchemaSchPreamble)) {
                return kicad_file_type_1.KicadFileType.EESchemaSch;
            }
            break;
        case '.lib':
            if (fileContents.startsWith(constants_1.EESchemaLibPreamble)) {
                return kicad_file_type_1.KicadFileType.EESchemaLib;
            }
            break;
        case 'fp-lib-table':
            if (fileContents.startsWith(constants_1.KicadFootprintLibTablePreamble)) {
                return kicad_file_type_1.KicadFileType.KicadFootprintLibTable;
            }
            break;
        case 'sym-lib-table':
            if (fileContents.startsWith(constants_1.KicadSymbolLibTablePreamble)) {
                return kicad_file_type_1.KicadFileType.KicadSymbolLibTable;
            }
            break;
        case '.dcm':
            if (fileContents.startsWith(constants_1.KicadDocLibPreamble)) {
                return kicad_file_type_1.KicadFileType.KicadDocLib;
            }
            break;
        case '.kicad_pro':
            try {
                const contents = JSON.parse(fileContents);
                if (contents.meta?.filename === filename) {
                    return kicad_file_type_1.KicadFileType.KicadProject;
                }
            }
            catch {
            }
            break;
        case '.kicad_prl':
            try {
                const contents = JSON.parse(fileContents);
                if (contents.meta?.filename === filename) {
                    return kicad_file_type_1.KicadFileType.KicadProjectLocalSettings;
                }
            }
            catch {
            }
            break;
        case '.net':
            return kicad_file_type_1.KicadFileType.KicadNetlist;
    }
    return false;
}
function validateKicadFile(filename, fileContents) {
    const type = getKicadFileType(filename, fileContents);
    if (!type) {
        return {
            isValid: false,
            data: undefined,
            errors: [`Invalid KiCad file or contents ${filename}`],
        };
    }
    let validationResult;
    let semanticType;
    switch (type) {
        case kicad_file_type_1.KicadFileType.KicadSch:
            validationResult = (0, v6schValidate_1.validateKicadSch)(filename, fileContents);
            semanticType = 'schematic';
            break;
        case kicad_file_type_1.KicadFileType.KicadPcb:
            validationResult = (0, boardValidate_1.validateKicadPcb)(filename, fileContents);
            semanticType = 'board';
            break;
        case kicad_file_type_1.KicadFileType.EESchemaSch:
            return {
                isValid: false,
                data: undefined,
                semanticType: 'schematic',
                errors: ['KiCad v5 schematics are not supported. Please upgrade to KiCad v6+'],
            };
        case kicad_file_type_1.KicadFileType.KicadFootprintLibTable:
        case kicad_file_type_1.KicadFileType.KicadSymbolLibTable:
            validationResult = (0, validateKicadV6SchemaFile_1.validateKicadV6SchemaFile)(fileContents, false, true);
            semanticType = 'other';
            break;
        case kicad_file_type_1.KicadFileType.KicadDocLib:
        case kicad_file_type_1.KicadFileType.EESchemaLib:
            return {
                isValid: false,
                data: undefined,
                semanticType: 'lib',
                errors: ['KiCad v5 libraries are not supported. Please upgrade to KiCad v6+'],
            };
        case kicad_file_type_1.KicadFileType.KicadProject:
        case kicad_file_type_1.KicadFileType.KicadProjectLocalSettings:
            validationResult = (0, projectValidation_1.validateKicadProjectFile)(filename, fileContents);
            semanticType = 'project';
            break;
        case kicad_file_type_1.KicadFileType.KicadNetlist:
            validationResult = (0, kicadNetlistValidation_1.validateKicadNetlist)(filename, fileContents);
            semanticType = 'netlist';
            break;
        default:
            semanticType = 'other';
            validationResult = {
                metadata: {},
                referencedFiles: [],
            };
    }
    if (validationResult.skipReason) {
        return {
            isValid: false,
            semanticType,
            errors: [validationResult.skipReason],
        };
    }
    return {
        isValid: true,
        data: {
            semanticType: semanticType,
            fileFormat: {
                cadTool: 'kicad',
                type: type,
                version: validationResult.version,
            },
            referencedFiles: validationResult.referencedFiles,
            metadata: validationResult.metadata,
        },
    };
}
function detectKicadProjects(files, fileDetails) {
    return (0, project_detection_1.detectAnchoredProjects)({
        tool: 'kicad',
        toolDisplayName: 'KiCad',
        files,
        fileDetails,
        primaryAnchorMatch: (0, project_detection_1.endsWithExt)('.kicad_pro'),
        primaryAnchorLabel: '.kicad_pro',
        secondaryAnchorMatch: (0, project_detection_1.endsWithExt)('.kicad_prl'),
        secondaryAnchorLabel: '.kicad_prl',
    });
}
//# sourceMappingURL=kicadValidation.js.map